import * as React from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  ChevronLeft, ChevronRight, Send, Copy, Play, Square, ClipboardList, User, Clock, Plus, Trash2, Save, CalendarDays,
  Activity, PoundSterling, Sparkles, ShieldAlert, CheckCheck,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { useAuth } from "@/context/AuthContext";
import DetailDrawer from "@/components/DetailDrawer";
import EvidenceBlock from "@/components/evidence/EvidenceBlock";
import { toast } from "sonner";
import {
  mondayOf, addDays, isoDate, fmtTime, fmtDayLabel, fmtCents, fmtDuration,
} from "@/lib/time";
import { Skeleton, SkeletonTable } from "@/components/feel/Skeleton";
import { EmptyState } from "@/components/feel/EmptyState";
import StaffLoopsPanel from "@/components/rotas/StaffLoopsPanel";

export default function Rotas() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "roster";
  return (
    <div className="px-4 md:px-6 py-4 md:py-6 max-w-[1400px]" data-testid="rotas-page">
      <div className="flex items-center gap-1 border-b border-brand-rule mb-4 overflow-x-auto">
        <Tab v="roster" cur={tab} setV={(v)=>setParams({tab:v})} icon={<ClipboardList size={13}/>}>Roster</Tab>
        <Tab v="live"   cur={tab} setV={(v)=>setParams({tab:v})} icon={<Activity size={13}/>}>Live</Tab>
        <Tab v="costs"  cur={tab} setV={(v)=>setParams({tab:v})} icon={<PoundSterling size={13}/>}>Costs</Tab>
        <Tab v="my"     cur={tab} setV={(v)=>setParams({tab:v})} icon={<User size={13}/>}>My shifts</Tab>
        <Tab v="times"  cur={tab} setV={(v)=>setParams({tab:v})} icon={<Clock size={13}/>}>Timesheets</Tab>
      </div>
      {tab === "roster" && <Roster />}
      {tab === "live"   && <RollCall />}
      {tab === "costs"  && <CostSiteWeek />}
      {tab === "my"     && <MyShifts />}
      {tab === "times"  && <Timesheets />}
    </div>
  );
}

function Tab({ v, cur, setV, children, icon }) {
  const active = v === cur;
  return (
    <button onClick={() => setV(v)}
            className={`px-3 py-2 text-[12.5px] font-medium border-b-2 flex items-center gap-1.5 ${active ? 'border-brand-violet text-brand-violet' : 'border-transparent text-brand-muted hover:text-brand-ink'}`}
            data-testid={`rotas-tab-${v}`}>
      {icon}{children}
    </button>
  );
}

// ---------------- ROSTER ----------------

function Roster() {
  const { companyId } = useParams();
  const { has } = useCompany();
  const [sites, setSites] = React.useState([]);
  const [siteId, setSiteId] = React.useState("");
  const [weekMonday, setWeekMonday] = React.useState(mondayOf());
  const [members, setMembers] = React.useState([]);
  const [shifts, setShifts] = React.useState([]);
  const [summary, setSummary] = React.useState(null);
  const [reconcile, setReconcile] = React.useState(null); // {scheduled_cents, actual_approved_cents}
  const [drawer, setDrawer] = React.useState(null); // {kind:'new'|'edit', shift, member_id, day_iso}
  const [busy, setBusy] = React.useState(false);
  const [initialLoad, setInitialLoad] = React.useState(true);
  const [autoBuildOpen, setAutoBuildOpen] = React.useState(false);

  const canManage = has("rotas.manage");
  const canPublish = has("rotas.publish");
  const canReconcile = has("pay.manage");
  const weekStartIso = isoDate(weekMonday);
  const days = React.useMemo(() => Array.from({length:7}, (_, i) => addDays(weekMonday, i)), [weekMonday]);

  // Load sites
  React.useEffect(() => {
    api.get(`/companies/${companyId}/sites`).then(({data}) => {
      setSites(data.sites);
      if (!siteId && data.sites.length) setSiteId(data.sites[0].id);
    });
  }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = React.useCallback(async () => {
    if (!siteId) return;
    setBusy(true);
    try {
      const winStart = weekMonday.toISOString();
      const winEnd = addDays(weekMonday, 7).toISOString();
      const [membersRes, shiftsRes, sumRes] = await Promise.all([
        api.get(`/companies/${companyId}/members`),
        api.get(`/companies/${companyId}/rotas/shifts`, {
          params: { site_id: siteId, start: winStart, end: winEnd },
        }),
        api.get(`/companies/${companyId}/rotas/week-summary`, {
          params: { site_id: siteId, week_start: weekStartIso },
        }),
      ]);
      // Only members assigned to this site (plus current members with any shift here)
      const activeSet = new Set(shiftsRes.data.shifts.filter(s => s.member_id).map(s => s.member_id));
      const filtered = membersRes.data.members.filter(m => m.site_ids.includes(siteId) || activeSet.has(m.user_id));
      filtered.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setMembers(filtered);
      setShifts(shiftsRes.data.shifts);
      setSummary(sumRes.data);
      // Reconcile — pay.manage only. Silent skip on 403.
      if (canReconcile) {
        try {
          const { data: r } = await api.get(`/companies/${companyId}/pay/reconcile-week`, {
            params: { site_id: siteId, week_start: weekStartIso },
          });
          setReconcile(r);
        } catch { setReconcile(null); }
      } else {
        setReconcile(null);
      }
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); setInitialLoad(false); }
  }, [companyId, siteId, weekStartIso, weekMonday, canReconcile]);

  React.useEffect(() => { load(); }, [load]);
  React.useEffect(() => {
    const on = (e) => { const t = e.detail?.type;
      if (t && (t.startsWith("shift.") || t.startsWith("rota."))) load();
    };
    window.addEventListener("fable:event", on);
    return () => window.removeEventListener("fable:event", on);
  }, [load]);

  async function publishWeek() {
    if (!canPublish) return;
    if (!window.confirm("Publish all draft shifts for this site + week?")) return;
    try {
      const { data } = await api.post(`/companies/${companyId}/rotas/publish`, {
        site_id: siteId, week_start: weekStartIso,
      });
      toast.success(`Published ${data.published_count}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  }
  async function copyPrev() {
    if (!canManage) return;
    if (!window.confirm("Copy previous week's shifts here as drafts?")) return;
    try {
      const { data } = await api.post(`/companies/${companyId}/rotas/copy-previous-week`, {
        site_id: siteId, week_start: weekStartIso,
      });
      toast.success(`Created ${data.created_count} draft`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  }

  // Group shifts by (member_id || 'open', day_iso)
  const shiftsByKey = React.useMemo(() => {
    const map = {};
    for (const s of shifts) {
      const dayIso = isoDate(new Date(s.start));
      const key = `${s.member_id || 'open'}::${dayIso}`;
      map[key] = map[key] || [];
      map[key].push(s);
    }
    return map;
  }, [shifts]);

  // Sort rows: real members first (already sorted), Open Shifts row last
  const rows = [
    ...members.map(m => ({ kind: 'member', id: m.user_id, name: m.name, role_name: m.role_name })),
    { kind: 'open', id: 'open', name: 'Open shifts', role_name: '' },
  ];

  return (
    <div data-testid="rotas-roster">
      {/* Header controls */}
      <div className="flex items-center gap-3 mb-3">
        <select className="field-input !w-56 !h-8" value={siteId}
                onChange={(e) => setSiteId(e.target.value)}
                data-testid="roster-site-select">
          {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <div className="flex items-center gap-1 rounded-md border border-brand-rule bg-card">
          <button className="btn-ghost !h-8 !px-2" onClick={() => setWeekMonday(addDays(weekMonday, -7))}
                  data-testid="roster-prev-week"><ChevronLeft size={14}/></button>
          <button className="btn-ghost !h-8 !px-2 mono-num text-[11px]"
                  onClick={() => setWeekMonday(mondayOf())}
                  data-testid="roster-today">
            Week of {weekStartIso}
          </button>
          <button className="btn-ghost !h-8 !px-2" onClick={() => setWeekMonday(addDays(weekMonday, 7))}
                  data-testid="roster-next-week"><ChevronRight size={14}/></button>
        </div>
        <div className="flex-1" />
        {canManage && (
          <button className="btn-outline" onClick={copyPrev} data-testid="roster-copy-prev">
            <Copy size={13}/> Copy previous week
          </button>
        )}
        {canPublish && (
          <button className="btn-outline"
                  onClick={()=>setAutoBuildOpen(true)} data-testid="roster-auto-build">
            <Sparkles size={13}/> Auto-build
          </button>
        )}
        {canPublish && (
          <button className="btn-violet" onClick={publishWeek}
                  disabled={!summary || summary.draft_count === 0}
                  data-testid="roster-publish">
            <Send size={13}/> Publish week
            {summary?.draft_count > 0 && (
              <span className="mono-num text-[10px] chip !bg-card/20 !text-white ml-1">
                {summary.draft_count}
              </span>
            )}
          </button>
        )}
      </div>

      {/* Summary strip */}
      {summary && (
        <div className="mb-3 grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="rotas-summary">
          <SumTile label="Hours"   value={summary.total_hours.toFixed(1)} suffix="h" />
          <SumTile label="Labour"  value={fmtCents(summary.labour_cents)} testid="sum-labour" />
          <SumTile label="Shifts"  value={summary.shift_count} />
          <SumTile label="Drafts"  value={summary.draft_count} tone={summary.draft_count > 0 ? 'amber' : ''} />
          <SumTile label="Open"    value={summary.open_shift_count} tone={summary.open_shift_count > 0 ? 'coral' : ''} />
        </div>
      )}

      {/* Labour-cost strip (per-day scheduled + week reconciliation) — Phase 5 */}
      {summary && (
        <LabourStrip days={days} summary={summary} reconcile={reconcile}/>
      )}

      {/* Grid */}
      <div className="card-panel table-scroll" data-testid="rotas-week-grid">
        {initialLoad ? (
          <div className="p-4">
            <SkeletonTable rows={5} cols={8} />
          </div>
        ) : rows.length === 1 && rows[0].kind === "open" && shifts.length === 0 ? (
          <EmptyState icon={CalendarDays} eyebrow="Rotas"
            title="Nothing scheduled this week."
            body="Add shifts inline in the grid, or copy last week to get started."
            action={canManage
              ? <button className="btn-outline" onClick={copyPrev} data-testid="roster-empty-copy">
                  <Copy size={13}/> Copy previous week
                </button>
              : null}
            testid="rotas-empty" />
        ) : (
        <table className="fable-table" style={{tableLayout: 'fixed', minWidth: '900px'}}>
          <thead>
            <tr>
              <th style={{width: 180}}>Member</th>
              {days.map((d) => (
                <th key={d.toISOString()} className="text-center">
                  <div className="text-[10px] tracking-[0.14em] text-brand-faint">{fmtDayLabel(d)}</div>
                  <div className="mono-num text-[10.5px] text-brand-muted">{isoDate(d).slice(5)}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>
                  <div className="flex items-center gap-2">
                    {row.kind === 'member' ? (
                      <span className="h-5 w-5 rounded-full bg-brand-violet text-white text-[9px] font-bold flex items-center justify-center">
                        {row.name.charAt(0).toUpperCase()}
                      </span>
                    ) : (
                      <span className="h-5 w-5 rounded-full bg-brand-amber/20 text-brand-amber text-[9px] font-bold flex items-center justify-center">?</span>
                    )}
                    <div className="min-w-0">
                      <div className="text-[12.5px] font-medium text-brand-ink truncate">{row.name}</div>
                      {row.role_name && <div className="text-[10.5px] text-brand-muted truncate">{row.role_name}</div>}
                    </div>
                  </div>
                </td>
                {days.map((d) => {
                  const dayIso = isoDate(d);
                  const key = `${row.id}::${dayIso}`;
                  const items = shiftsByKey[key] || [];
                  return (
                    <td key={dayIso} className="align-top" style={{padding: 4, minHeight: 60}}>
                      <div className="min-h-[52px]">
                        {items.map((s) => (
                          <button key={s.id} onClick={() => setDrawer({kind:'edit', shift:s})}
                                  className={`w-full text-left px-1.5 py-1 mb-1 rounded ${s.status === 'published' ? 'bg-brand-violet text-white' : 'bg-card border border-dashed border-brand-violet text-brand-violet'}`}
                                  data-testid={`shift-chip-${s.id}`}>
                            <div className="mono-num text-[10.5px]">
                              {fmtTime(s.start)}–{fmtTime(s.end)}
                            </div>
                            {s.position && <div className="text-[10px] opacity-80 truncate">{s.position}</div>}
                          </button>
                        ))}
                        {canManage && (
                          <button className="w-full opacity-0 hover:opacity-100 focus:opacity-100 text-[10px] text-brand-muted hover:text-brand-violet transition-opacity mt-1 border border-dashed border-brand-rule rounded py-0.5"
                                  onClick={() => setDrawer({
                                    kind: 'new', shift: null,
                                    member_id: row.kind === 'member' ? row.id : null,
                                    site_id: siteId, day_iso: dayIso,
                                  })}
                                  data-testid={`rotas-cell-${row.id}-${dayIso}`}>
                            <Plus size={11} className="inline"/> add
                          </button>
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        )}
      </div>

      {/* Shift drawer */}
      <DetailDrawer open={!!drawer} onOpenChange={(v) => { if (!v) setDrawer(null); }}
                    title={drawer?.kind === 'new' ? 'New shift' : 'Edit shift'}
                    subtitle={drawer?.kind === 'edit' ? drawer.shift.status : ''}
                    testid="shift-drawer">
        {drawer && <ShiftForm data={drawer} sites={sites} members={members} companyId={companyId}
                              canManage={canManage} canPublish={canPublish}
                              onDone={() => { setDrawer(null); load(); }} />}
      </DetailDrawer>

      <DetailDrawer open={autoBuildOpen} onClose={()=>setAutoBuildOpen(false)}
                    title="Auto-build week (lite)"
                    subtitle="Greedy heuristic · review before applying"
                    testid="auto-build-drawer">
        {autoBuildOpen && (
          <AutoBuildPanel companyId={companyId} siteId={siteId}
                          weekStart={weekStartIso}
                          onApplied={()=>{ setAutoBuildOpen(false); load(); }}/>
        )}
      </DetailDrawer>
    </div>
  );
}

function SumTile({ label, value, suffix, tone, testid }) {
  const toneClass = tone === 'amber' ? 'text-amber-700' : tone === 'coral' ? 'text-brand-coral' : 'text-brand-ink';
  return (
    <div className="card-panel px-3 py-2" data-testid={testid}>
      <div className="micro-label">{label}</div>
      <div className="mt-0.5 flex items-baseline gap-1">
        <span className={`mono-num text-[16px] font-bold ${toneClass}`}>{value}</span>
        {suffix && <span className="mono-num text-[11px] text-brand-muted">{suffix}</span>}
      </div>
    </div>
  );
}

// Phase 5 — labour-cost strip. Per-day scheduled totals (mono) plus a
// week total on the right. When approved time entries exist for the week,
// the honest approved-actual figure is shown alongside — that's the
// reconciliation signal, not a KPI.
function LabourStrip({ days, summary, reconcile }) {
  const byDay = summary?.labour_by_day || {};
  const weekScheduled = summary?.labour_cents || 0;
  const actual = reconcile?.actual_approved_cents || 0;
  const hasActual = actual > 0;
  return (
    <div className="mb-3 card-panel table-scroll" data-testid="rotas-labour-strip">
      <div className="grid" style={{gridTemplateColumns: "180px repeat(7, minmax(0, 1fr)) 200px", minWidth: "900px"}}>
        <div className="px-3 py-2 border-r border-brand-rule flex flex-col justify-center">
          <div className="micro-label">Labour</div>
          <div className="text-[10.5px] text-brand-muted">Scheduled cost</div>
        </div>
        {days.map((d) => {
          const key = isoDate(d);
          const cents = byDay[key] || 0;
          return (
            <div key={key} className="px-2 py-2 border-r border-brand-rule text-center"
                 data-testid={`labour-day-${key}`}>
              <div className={`mono-num text-[13px] font-semibold ${cents > 0 ? 'text-brand-ink' : 'text-brand-faint'}`}>
                {fmtCents(cents)}
              </div>
            </div>
          );
        })}
        <div className="px-3 py-2 flex flex-col items-end justify-center bg-brand-canvas-3">
          <div className="mono-num text-[15px] font-bold text-brand-violet-lo" data-testid="labour-week-total">
            {fmtCents(weekScheduled)}
          </div>
          <div className="mono-num text-[10.5px] text-brand-muted">week scheduled</div>
          {hasActual && (
            <div className="mt-1 flex items-center gap-1" data-testid="labour-week-actual">
              <span className="chip chip-emerald mono-num text-[10px]">actual</span>
              <span className="mono-num text-[11.5px] text-brand-ink">{fmtCents(actual)}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ShiftForm({ data, sites, members, companyId, canManage, canPublish, onDone }) {
  const isEdit = data.kind === 'edit';
  const initial = isEdit ? {
    site_id: data.shift.site_id,
    member_id: data.shift.member_id || "",
    start: toLocalInput(data.shift.start),
    end:   toLocalInput(data.shift.end),
    position: data.shift.position || "",
    notes: data.shift.notes || "",
    status: data.shift.status,
  } : {
    site_id: data.site_id,
    member_id: data.member_id || "",
    start: `${data.day_iso}T09:00`,
    end:   `${data.day_iso}T17:00`,
    position: "",
    notes: "",
    status: "draft",
  };
  const [f, setF] = React.useState(initial);
  const [busy, setBusy] = React.useState(false);

  async function save() {
    if (busy) return;
    setBusy(true);
    try {
      const payload = {
        site_id: f.site_id,
        member_id: f.member_id || null,
        start: new Date(f.start).toISOString(),
        end: new Date(f.end).toISOString(),
        position: f.position, notes: f.notes,
        status: f.status,
      };
      if (isEdit) {
        await api.patch(`/companies/${companyId}/rotas/shifts/${data.shift.id}`, payload);
        toast.success("Saved");
      } else {
        await api.post(`/companies/${companyId}/rotas/shifts`, payload);
        toast.success("Created");
      }
      onDone();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "member_record_lapsed") {
        toast.error(`Blocked — ${d.type_label || "a blocking record"} has lapsed for this member.`);
      } else {
        toast.error(formatApiErrorDetail(d));
      }
    }
    finally { setBusy(false); }
  }
  async function del() {
    if (!isEdit) return;
    if (!window.confirm("Delete this shift?")) return;
    try {
      await api.delete(`/companies/${companyId}/rotas/shifts/${data.shift.id}`);
      toast.success("Removed");
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  }

  return (
    <div className="p-5 space-y-3">
      <div>
        <label className="micro-label">Site</label>
        <select className="field-input mt-1" value={f.site_id}
                onChange={(e)=>setF(x=>({...x,site_id:e.target.value}))} disabled={!canManage}
                data-testid="shift-site">
          {sites.map((s)=><option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>
      <div>
        <label className="micro-label">Member</label>
        <select className="field-input mt-1" value={f.member_id}
                onChange={(e)=>setF(x=>({...x,member_id:e.target.value}))} disabled={!canManage}
                data-testid="shift-member">
          <option value="">— Open shift —</option>
          {members.map((m)=><option key={m.user_id} value={m.user_id}>{m.name}</option>)}
        </select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="micro-label">Start</label>
          <input type="datetime-local" className="field-input mt-1 mono-num" value={f.start}
                 onChange={(e)=>setF(x=>({...x,start:e.target.value}))} disabled={!canManage}
                 data-testid="shift-start" />
        </div>
        <div>
          <label className="micro-label">End</label>
          <input type="datetime-local" className="field-input mt-1 mono-num" value={f.end}
                 onChange={(e)=>setF(x=>({...x,end:e.target.value}))} disabled={!canManage}
                 data-testid="shift-end" />
        </div>
      </div>
      <div>
        <label className="micro-label">Position</label>
        <input className="field-input mt-1" value={f.position}
               onChange={(e)=>setF(x=>({...x,position:e.target.value}))} disabled={!canManage}
               data-testid="shift-position" />
      </div>
      <div>
        <label className="micro-label">Notes</label>
        <textarea className="field-input mt-1 !h-16" value={f.notes}
                  onChange={(e)=>setF(x=>({...x,notes:e.target.value}))} disabled={!canManage}
                  data-testid="shift-notes" />
      </div>
      <div>
        <label className="micro-label">Status</label>
        <select className="field-input mt-1" value={f.status}
                onChange={(e)=>setF(x=>({...x,status:e.target.value}))}
                disabled={!canManage || (f.status !== 'published' && !canPublish)}
                data-testid="shift-status">
          <option value="draft">Draft</option>
          <option value="published" disabled={!canPublish}>Published</option>
        </select>
      </div>
      {canManage && (
        <div className="pt-3 border-t border-brand-rule flex items-center gap-2">
          <button className="btn-violet" onClick={save} disabled={busy}
                  data-testid="shift-save">
            <Save size={13}/> {busy ? "…" : (isEdit ? "Save" : "Create")}
          </button>
          {isEdit && (
            <button className="btn-outline text-brand-coral border-brand-coral/40"
                    onClick={del} data-testid="shift-delete">
              <Trash2 size={13}/> Delete
            </button>
          )}
        </div>
      )}
      {isEdit && (
        <EvidenceBlock
          companyId={companyId}
          attachedType="shift"
          attachedId={data.shift.id}
          canEdit={canManage}
          testidPrefix="shift-evidence"
        />
      )}
    </div>
  );
}

function toLocalInput(iso) {
  const d = new Date(iso);
  // datetime-local expects "YYYY-MM-DDTHH:MM" in local time; use UTC to avoid TZ drift in dev
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getUTCFullYear()}-${p(d.getUTCMonth()+1)}-${p(d.getUTCDate())}T${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`;
}

// ---------------- MY SHIFTS ----------------

function MyShifts() {
  const { companyId } = useParams();
  const { user } = useAuth();
  const { has } = useCompany();
  const [rows, setRows] = React.useState(null);
  const [sites, setSites] = React.useState([]);
  const [open, setOpen] = React.useState(null);
  const [siteChoice, setSiteChoice] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const canClaim = has("rotas.claim");

  const load = React.useCallback(async () => {
    const [{data: my}, {data: ss}, {data: te}] = await Promise.all([
      api.get(`/companies/${companyId}/rotas/my-shifts`),
      api.get(`/companies/${companyId}/sites`),
      // Attempt to fetch open entry; endpoint requires rotas.manage so fallback to null on 403
      Promise.resolve({ data: null }),
    ]);
    setRows(my.shifts);
    setSites(ss.sites);
    if (!siteChoice && ss.sites.length) setSiteChoice(ss.sites[0].id);
    // Detect our currently-open time entry by trying to clock-in and rolling back on 409
    // Cheaper: expose GET /rotas/my-open would be ideal, but for MVP we just track locally.
  }, [companyId, siteChoice]);

  React.useEffect(() => { load(); }, [load]);

  async function clockIn() {
    if (!siteChoice || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/companies/${companyId}/rotas/clock-in`, { site_id: siteChoice });
      setOpen(data);
      toast.success("Clocked in");
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "already_clocked_in") {
        setOpen({ id: d.open_entry_id, clock_in_at: d.clock_in_at });
        toast.info("Already clocked in");
      } else {
        toast.error(formatApiErrorDetail(d));
      }
    } finally { setBusy(false); }
  }
  async function clockOut() {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/companies/${companyId}/rotas/clock-out`);
      setOpen(null);
      toast.success(`Clocked out (${fmtDuration(data.duration_minutes)})`);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  }

  if (!rows) return <div className="text-brand-muted text-[12px]" data-testid="myshifts-loading">Loading…</div>;

  return (
    <div data-testid="rotas-my-shifts">
      <div className="card-panel mb-4">
        <div className="card-panel-head">
          <div>
            <div className="micro-label">Clock</div>
            <div className="mt-0.5 text-[13px] font-semibold text-brand-ink">
              {open ? <>You're clocked in since <span className="mono-num">{new Date(open.clock_in_at).toISOString().slice(11,16)}</span></> : "Not clocked in"}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select className="field-input !w-40 !h-8" value={siteChoice}
                    onChange={(e)=>setSiteChoice(e.target.value)}
                    disabled={!!open} data-testid="myshifts-site">
              {sites.map((s)=><option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            {open ? (
              <button className="btn-outline text-brand-coral border-brand-coral/40"
                      onClick={clockOut} disabled={busy} data-testid="myshifts-clock-out">
                <Square size={13}/> Clock out
              </button>
            ) : (
              <button className="btn-violet" onClick={clockIn} disabled={busy || !siteChoice}
                      data-testid="myshifts-clock-in">
                <Play size={13}/> Clock in
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="card-panel overflow-hidden">
        <div className="table-scroll"><table className="fable-table" data-testid="myshifts-table">
          <thead>
            <tr>
              <th style={{width: "26%"}}>Start</th>
              <th style={{width: "12%"}}>End</th>
              <th style={{width: "26%"}}>Site</th>
              <th>Position</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.id} data-testid={`myshifts-row-${s.id}`}>
                <td className="mono-num text-[12px] text-brand-ink">
                  {new Date(s.start).toISOString().replace('T', ' ').slice(0, 16)}
                </td>
                <td className="mono-num text-[12px] text-brand-muted">
                  {new Date(s.end).toISOString().slice(11, 16)}
                </td>
                <td>
                  <span className="chip" style={{background: s.site_color + '22', color: s.site_color}}>{s.site_name}</span>
                </td>
                <td className="text-[12px] text-brand-ink">{s.position || '—'}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={4} className="text-center py-10 text-[12px] text-brand-muted">
                No upcoming published shifts.
              </td></tr>
            )}
          </tbody>
        </table></div>
      </div>

      {/* Phase 10.1 — staff loops: open shifts / swaps / availability */}
      <div className="mt-6">
        <StaffLoopsPanel companyId={companyId} canClaim={canClaim} onChanged={load} />
      </div>
    </div>
  );
}

// ---------------- TIMESHEETS ----------------

function Timesheets() {
  const { companyId } = useParams();
  const { has } = useCompany();
  const canReview = has("rotas.publish");
  const [mode, setMode] = React.useState(canReview ? "exceptions" : "all");
  const [rows, setRows] = React.useState(null);
  const [excData, setExcData] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  const loadAll = React.useCallback(async () => {
    try {
      const { data } = await api.get(`/companies/${companyId}/rotas/time-entries`);
      setRows(data.time_entries);
    } catch (e) { setError(e?.response?.data?.detail); }
  }, [companyId]);

  const loadExc = React.useCallback(async () => {
    try {
      const { data } = await api.get(`/companies/${companyId}/rotas/timesheets/exception-only`);
      setExcData(data);
    } catch (e) { setError(e?.response?.data?.detail); }
  }, [companyId]);

  React.useEffect(() => {
    if (mode === "exceptions") loadExc();
    else loadAll();
  }, [mode, loadAll, loadExc]);

  async function bulkApprove() {
    if (!excData?.clean_pending_ids?.length || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/companies/${companyId}/rotas/timesheets/bulk-approve`, {
        time_entry_ids: excData.clean_pending_ids,
      });
      toast.success(`Approved ${data.approved} clean row(s)`);
      loadExc();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  }

  if (error) return <div className="p-6 text-[12px] text-brand-coral" data-testid="timesheets-forbidden">
    {formatApiErrorDetail(error)}
  </div>;

  const displayRows = mode === "exceptions" ? (excData?.exceptions || null) : rows;
  if (!displayRows && mode !== "exceptions") return <div className="text-brand-muted text-[12px]" data-testid="timesheets-loading">Loading…</div>;
  if (mode === "exceptions" && !excData) return <div className="text-brand-muted text-[12px]" data-testid="timesheets-loading">Loading…</div>;

  const totalMin = (displayRows || []).reduce((a, r) => a + (r.duration_minutes || 0), 0);

  return (
    <div data-testid="rotas-timesheets">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="text-[12px] text-brand-muted">
          {mode === "exceptions" ? (
            <>
              <span className="mono-num text-brand-ink">{excData?.count_exceptions ?? 0}</span> exceptions ·
              <span className="mono-num text-brand-ink"> {excData?.count_clean_pending ?? 0}</span> clean pending ·
              <span className="mono-num text-brand-ink"> {excData?.total_scanned ?? 0}</span> total scanned
            </>
          ) : (
            <>
              <span className="mono-num text-brand-ink">{rows?.length || 0}</span> entries ·
              <span className="mono-num text-brand-ink"> {fmtDuration(totalMin)}</span> total
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          {canReview && (
            <div className="inline-flex rounded-lg border border-brand-rule overflow-hidden">
              <button onClick={()=>setMode("exceptions")}
                      className={`px-3 h-8 text-[12px] flex items-center gap-1.5 ${mode==="exceptions" ? 'bg-brand-violet text-white' : 'text-brand-muted hover:text-brand-ink'}`}
                      data-testid="timesheets-mode-exceptions">
                <ShieldAlert size={12}/> Exceptions
              </button>
              <button onClick={()=>setMode("all")}
                      className={`px-3 h-8 text-[12px] flex items-center gap-1.5 ${mode==="all" ? 'bg-brand-violet text-white' : 'text-brand-muted hover:text-brand-ink'}`}
                      data-testid="timesheets-mode-all">
                All
              </button>
            </div>
          )}
          {mode === "exceptions" && (excData?.clean_pending_ids?.length > 0) && (
            <button onClick={bulkApprove} disabled={busy}
                    className="btn-violet" data-testid="timesheets-bulk-approve">
              <CheckCheck size={13}/> Approve {excData.clean_pending_ids.length} clean row(s)
            </button>
          )}
        </div>
      </div>
      <div className="card-panel overflow-hidden">
        <div className="table-scroll"><table className="fable-table" data-testid="timesheets-table">
          <thead>
            <tr>
              <th style={{width: "18%"}}>Person</th>
              <th style={{width: "20%"}}>Clock in</th>
              <th style={{width: "20%"}}>Clock out</th>
              <th style={{width: "10%"}}>Duration</th>
              <th>{mode === "exceptions" ? "Reasons" : "Exceptions"}</th>
            </tr>
          </thead>
          <tbody>
            {(displayRows || []).map((r) => (
              <tr key={r.id} data-testid={`timesheet-row-${r.id}`}>
                <td className="font-medium text-brand-ink">{r.user_name || r.user_id?.slice(0,8)}</td>
                <td className="mono-num text-[11.5px] text-brand-muted">
                  {r.clock_in_at?.replace('T', ' ').slice(0, 16)}
                </td>
                <td className="mono-num text-[11.5px] text-brand-muted">
                  {r.clock_out_at ? r.clock_out_at.replace('T', ' ').slice(0, 16) :
                    <span className="chip chip-emerald">open</span>}
                </td>
                <td className="mono-num text-[12px] text-brand-ink">{fmtDuration(r.duration_minutes)}</td>
                <td>
                  <div className="flex flex-wrap gap-1">
                    {((mode === "exceptions" ? r.exception_reasons : r.exceptions) || []).map((x) => (
                      <span key={x} className={`chip ${x === 'no_scheduled_shift' ? 'chip-amber' : 'chip-coral'}`}>{x}</span>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
            {(displayRows || []).length === 0 && (
              <tr><td colSpan={5} className="text-center text-brand-muted text-[12px] py-8">
                {mode === "exceptions" ? "No exceptions — clean sweep." : "No entries."}
              </td></tr>
            )}
          </tbody>
        </table></div>
      </div>
    </div>
  );
}

// ---------------- LIVE ROLL-CALL ----------------

function RollCall() {
  const { companyId } = useParams();
  const [sites, setSites] = React.useState([]);
  const [siteId, setSiteId] = React.useState("");
  const [data, setData] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    api.get(`/companies/${companyId}/sites`).then(({data})=>setSites(data.sites));
  }, [companyId]);

  const load = React.useCallback(async () => {
    try {
      const params = siteId ? { site_id: siteId } : {};
      const { data } = await api.get(`/companies/${companyId}/rotas/roll-call`, { params });
      setData(data);
    } catch (e) { setError(e?.response?.data?.detail); }
  }, [companyId, siteId]);

  React.useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [load]);

  async function runNoShowSweep() {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/companies/${companyId}/rotas/ops/no-show-sweep`);
      toast.success(`Sweep done — ${data.new_events} new no-show event(s)`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  }

  if (error) return <div className="p-6 text-[12px] text-brand-coral" data-testid="rollcall-forbidden">{formatApiErrorDetail(error)}</div>;
  if (!data) return <div className="text-brand-muted text-[12px]" data-testid="rollcall-loading">Loading…</div>;

  return (
    <div data-testid="rotas-rollcall">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="text-[12px] text-brand-muted">
          <span className="mono-num text-brand-ink">{data.count}</span> clocked in ·
          <span className="mono-num text-brand-ink"> as of {(data.as_of || "").replace('T',' ').slice(11,19)}</span>
        </div>
        <div className="flex items-center gap-2">
          <select className="field-input !h-8 !w-44" value={siteId}
                  onChange={(e)=>setSiteId(e.target.value)}
                  data-testid="rollcall-site-filter">
            <option value="">All sites</option>
            {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <button onClick={runNoShowSweep} disabled={busy}
                  className="btn-outline" data-testid="rollcall-noshow-sweep">
            <ShieldAlert size={13}/> Run no-show sweep
          </button>
        </div>
      </div>
      <div className="card-panel overflow-hidden">
        <div className="table-scroll"><table className="fable-table" data-testid="rollcall-table">
          <thead>
            <tr>
              <th style={{width: "26%"}}>Person</th>
              <th style={{width: "22%"}}>Site</th>
              <th style={{width: "22%"}}>Since</th>
              <th style={{width: "12%"}}>Elapsed</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.time_entry_id} data-testid={`rollcall-row-${r.time_entry_id}`}>
                <td className="font-medium text-brand-ink">{r.user_name || r.user_id?.slice(0,8)}</td>
                <td>
                  <span className="chip" style={{background: r.site_color ? `${r.site_color}15` : undefined, color: r.site_color}}>
                    {r.site_name || "—"}
                  </span>
                </td>
                <td className="mono-num text-[11.5px] text-brand-muted">
                  {(r.clock_in_at || "").replace('T', ' ').slice(0, 16)}
                </td>
                <td className="mono-num text-[12px] text-brand-ink">
                  {r.elapsed_minutes != null ? fmtDuration(r.elapsed_minutes) : "—"}
                </td>
                <td>
                  <div className="flex flex-wrap gap-1">
                    {(r.exceptions || []).map((x) => (
                      <span key={x} className={`chip ${x === 'no_scheduled_shift' ? 'chip-amber' : 'chip-coral'}`}>{x}</span>
                    ))}
                    {!r.shift_id && <span className="chip chip-amber">no-plan</span>}
                  </div>
                </td>
              </tr>
            ))}
            {data.rows.length === 0 && (
              <tr><td colSpan={5} className="text-center text-brand-muted text-[12px] py-8">
                Nobody currently clocked in.
              </td></tr>
            )}
          </tbody>
        </table></div>
      </div>
    </div>
  );
}

// ---------------- COST V2 — site-week planned vs actual w/ on-costs ----------------

function CostSiteWeek() {
  const { companyId } = useParams();
  const [sites, setSites] = React.useState([]);
  const [siteId, setSiteId] = React.useState("");
  const [weekMonday, setWeekMonday] = React.useState(mondayOf());
  const [data, setData] = React.useState(null);
  const [policy, setPolicy] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    api.get(`/companies/${companyId}/sites`).then(({data})=>{
      setSites(data.sites);
      if (!siteId && data.sites.length) setSiteId(data.sites[0].id);
    });
    api.get(`/companies/${companyId}/rotas/cost/oncost-policy`).then(({data})=>setPolicy(data));
  }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const weekStartIso = isoDate(weekMonday);
  const load = React.useCallback(async () => {
    if (!siteId) return;
    try {
      const { data } = await api.get(`/companies/${companyId}/rotas/cost/site-week`, {
        params: { site_id: siteId, week_start: weekStartIso },
      });
      setData(data);
    } catch (e) { setError(e?.response?.data?.detail); }
  }, [companyId, siteId, weekStartIso]);
  React.useEffect(() => { load(); }, [load]);

  async function savePolicy(field, val) {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.patch(`/companies/${companyId}/rotas/cost/oncost-policy`, {
        [field]: Number(val),
      });
      setPolicy(data);
      toast.success("On-cost policy updated");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  }

  if (error) return <div className="p-6 text-[12px] text-brand-coral" data-testid="costs-forbidden">{formatApiErrorDetail(error)}</div>;

  const p = data?.planned || {};
  const a = data?.actual || {};
  return (
    <div data-testid="rotas-costs">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <select className="field-input !h-8 !w-44" value={siteId}
                onChange={(e)=>setSiteId(e.target.value)} data-testid="costs-site">
          {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <div className="flex items-center gap-1">
          <button className="btn-ghost !h-8 !w-8"
                  onClick={()=>setWeekMonday(addDays(weekMonday, -7))}
                  data-testid="costs-prev-week"><ChevronLeft size={14}/></button>
          <div className="text-[12px] mono-num text-brand-ink px-2">
            {weekStartIso}
          </div>
          <button className="btn-ghost !h-8 !w-8"
                  onClick={()=>setWeekMonday(addDays(weekMonday, 7))}
                  data-testid="costs-next-week"><ChevronRight size={14}/></button>
        </div>
      </div>

      {/* On-cost policy strip */}
      {policy && (
        <div className="card-panel mb-4" data-testid="costs-policy">
          <div className="card-panel-head">
            <div>
              <div className="micro-label">Employer on-costs (per-workspace)</div>
              <div className="text-[11.5px] text-brand-muted mt-0.5">Applied to every labour line — planned and actual.</div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4 p-4">
            <PolicyField label="Employer NI %" value={policy.employer_ni_pct}
              onSave={(v)=>savePolicy("employer_ni_pct", v)} testId="policy-ni"/>
            <PolicyField label="Pension %" value={policy.employer_pension_pct}
              onSave={(v)=>savePolicy("employer_pension_pct", v)} testId="policy-pension"/>
            <PolicyField label="Holiday accrual %" value={policy.holiday_accrual_pct}
              onSave={(v)=>savePolicy("holiday_accrual_pct", v)} testId="policy-holiday"/>
          </div>
        </div>
      )}

      {!data ? <div className="text-brand-muted text-[12px]" data-testid="costs-loading">Loading…</div> : (
        <>
          {/* Planned vs actual strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="costs-strip">
            <StatTile label="Planned hours" value={<span className="mono-num">{p.hours?.toFixed(1) || '0.0'}</span>}/>
            <StatTile label="Actual hours"  value={<span className="mono-num">{a.hours?.toFixed(1) || '0.0'}</span>}/>
            <StatTile label="Planned cost (loaded)" value={fmtCents(p.total_cents)} tone="violet"/>
            <StatTile label="Actual cost (loaded)"  value={fmtCents(a.total_cents)} tone="emerald"/>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
            <StatTile label="Variance (actual − planned)"
              value={<span className={data.variance_cents > 0 ? 'text-brand-coral' : 'text-brand-ink'}>{fmtCents(data.variance_cents)}</span>}/>
            <StatTile label="Wage % of revenue (planned)"
              value={<span className="mono-num">{data.wage_pct_of_revenue_planned != null ? data.wage_pct_of_revenue_planned.toFixed(1) + "%" : "—"}</span>}/>
            <StatTile label="Wage % of revenue (actual)"
              value={<span className="mono-num">{data.wage_pct_of_revenue_actual != null ? data.wage_pct_of_revenue_actual.toFixed(1) + "%" : "—"}</span>}/>
          </div>

          {/* Breakdown table */}
          <div className="card-panel overflow-hidden">
            <div className="table-scroll"><table className="fable-table" data-testid="costs-breakdown">
              <thead>
                <tr>
                  <th></th>
                  <th className="text-right">Base pay</th>
                  <th className="text-right">Employer NI</th>
                  <th className="text-right">Pension</th>
                  <th className="text-right">Holiday accrual</th>
                  <th className="text-right">Total loaded</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="font-medium text-brand-ink">Planned</td>
                  <td className="text-right mono-num">{fmtCents(p.base_cents)}</td>
                  <td className="text-right mono-num">{fmtCents(p.ni_cents)}</td>
                  <td className="text-right mono-num">{fmtCents(p.pension_cents)}</td>
                  <td className="text-right mono-num">{fmtCents(p.holiday_cents)}</td>
                  <td className="text-right mono-num font-semibold">{fmtCents(p.total_cents)}</td>
                </tr>
                <tr>
                  <td className="font-medium text-brand-ink">Actual (approved)</td>
                  <td className="text-right mono-num">{fmtCents(a.base_cents)}</td>
                  <td className="text-right mono-num">{fmtCents(a.ni_cents)}</td>
                  <td className="text-right mono-num">{fmtCents(a.pension_cents)}</td>
                  <td className="text-right mono-num">{fmtCents(a.holiday_cents)}</td>
                  <td className="text-right mono-num font-semibold">{fmtCents(a.total_cents)}</td>
                </tr>
              </tbody>
            </table></div>
          </div>
        </>
      )}
    </div>
  );
}

function PolicyField({ label, value, onSave, testId }) {
  const [v, setV] = React.useState(String(value ?? ""));
  const [editing, setEditing] = React.useState(false);
  React.useEffect(() => setV(String(value ?? "")), [value]);
  return (
    <div>
      <div className="micro-label">{label}</div>
      {!editing ? (
        <div className="mt-1 flex items-center gap-2">
          <div className="mono-num text-[15px] text-brand-ink" data-testid={testId}>{value}%</div>
          <button className="btn-ghost !h-6 !text-[11px]" onClick={()=>setEditing(true)}>Edit</button>
        </div>
      ) : (
        <div className="mt-1 flex items-center gap-2">
          <input className="field-input !h-8 !w-24 mono-num text-right"
                 type="number" step="0.01" value={v}
                 onChange={(e)=>setV(e.target.value)}/>
          <button className="btn-violet !h-8 !text-[11px]"
                  onClick={()=>{ onSave(v); setEditing(false); }}>Save</button>
          <button className="btn-ghost !h-8 !text-[11px]" onClick={()=>{ setV(String(value)); setEditing(false); }}>Cancel</button>
        </div>
      )}
    </div>
  );
}

function StatTile({ label, value, tone }) {
  const c = tone === "violet" ? "text-brand-violet" : tone === "emerald" ? "text-emerald-600" : "text-brand-ink";
  return (
    <div className="card-panel p-3">
      <div className="micro-label">{label}</div>
      <div className={`mt-0.5 text-[16px] font-semibold ${c}`}>{value}</div>
    </div>
  );
}


// ---------------- AUTO-BUILD DRAWER ----------------

function AutoBuildPanel({ companyId, siteId, weekStart, onApplied }) {
  const [data, setData] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [selected, setSelected] = React.useState({}); // shift_id -> user_id
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    setBusy(true);
    api.post(`/companies/${companyId}/rotas/auto-build`, {
      site_id: siteId, week_start: weekStart,
    }).then(({ data }) => {
      if (cancelled) return;
      setData(data);
      const s = {};
      for (const p of data.proposals || []) {
        if (p.proposed_user_id) s[p.shift_id] = p.proposed_user_id;
      }
      setSelected(s);
    }).catch(e => setError(e?.response?.data?.detail))
      .finally(() => !cancelled && setBusy(false));
    return () => { cancelled = true; };
  }, [companyId, siteId, weekStart]);

  async function applyAll() {
    if (busy) return;
    setBusy(true);
    try {
      const assignments = Object.entries(selected)
        .filter(([_, uid]) => !!uid)
        .map(([shift_id, user_id]) => {
          const p = data.proposals.find(p => p.shift_id === shift_id);
          return { shift_id, user_id, explain: p?.explain };
        });
      if (!assignments.length) { toast.info("Nothing selected to apply."); return; }
      const { data: r } = await api.post(`/companies/${companyId}/rotas/auto-build/apply`, { assignments });
      toast.success(`Applied ${r.applied_count} assignment(s)`);
      onApplied?.();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  }

  if (error) return <div className="p-4 text-[12px] text-brand-coral">{formatApiErrorDetail(error)}</div>;
  if (!data) return <div className="p-4 text-brand-muted text-[12px]">Computing proposals…</div>;

  return (
    <div className="p-4 space-y-3" data-testid="auto-build-panel">
      <div className="text-[12px] text-brand-muted">
        <span className="mono-num text-brand-ink">{data.count}</span> open drafts scanned · {data.note}
      </div>
      {data.count === 0 && (
        <div className="text-brand-muted text-[12.5px]">No open draft shifts for this site + week. Draft some first, or apply a pattern.</div>
      )}
      <div className="space-y-2">
        {(data.proposals || []).map((p) => (
          <div key={p.shift_id} className="card-panel p-3" data-testid={`auto-build-row-${p.shift_id}`}>
            <div className="flex items-center justify-between gap-2">
              <div>
                <div className="mono-num text-[12px] text-brand-ink">
                  {p.shift_start?.slice(5, 16).replace('T', ' ')} → {p.shift_end?.slice(11, 16)}
                </div>
                <div className="text-[11px] text-brand-muted">{p.position || '—'}</div>
              </div>
              <select className="field-input !h-8 !w-56"
                      value={selected[p.shift_id] || ""}
                      onChange={(e)=>setSelected(s=>({...s, [p.shift_id]: e.target.value}))}>
                <option value="">— leave open —</option>
                {(p.candidates || []).map(c => (
                  <option key={c.user_id} value={c.user_id} disabled={c.score < 0}>
                    {c.user_name} · score {c.score}
                  </option>
                ))}
              </select>
            </div>
            <div className="mt-1.5 text-[11px] text-brand-muted italic">{p.explain}</div>
          </div>
        ))}
      </div>
      {data.count > 0 && (
        <div className="pt-2">
          <button className="btn-violet w-full" onClick={applyAll} disabled={busy}
                  data-testid="auto-build-apply">
            <CheckCheck size={13}/> Apply {Object.values(selected).filter(Boolean).length} selection(s)
          </button>
        </div>
      )}
    </div>
  );
}

